import { useState, useEffect, useCallback, useRef } from 'react'
import { X, CaretLeft, CaretRight, Camera, Images, Image as ImageIcon, Spinner, ArrowsClockwise } from '@phosphor-icons/react'
import { LayerCard, Button } from '@cloudflare/kumo'
import type { GalleryImage, ImageListResponse } from '../types'

const PER_PAGE = 30
const API_BASE = '/api'

export function Gallery() {
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)

  const loadImages = useCallback(async (pageNum: number) => {
    if (loadingRef.current) return
    loadingRef.current = true
    
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE}/images?page=${pageNum}&perPage=${PER_PAGE}`)
      if (!response.ok) throw new Error('Failed to load images')
      const data: ImageListResponse = await response.json()
      
      if (pageNum === 1) {
        setImages(data.images)
      } else {
        setImages(prev => [...prev, ...data.images])
      }
      setTotal(data.total)
      setHasMore(data.hasMore)
      setPage(pageNum)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images')
    } finally {
      setLoading(false)
      setInitialLoading(false)
      loadingRef.current = false
    }
  }, [])

  useEffect(() => {
    loadImages(1)
  }, [loadImages])

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          loadImages(page + 1)
        }
      },
      { rootMargin: '400px' }
    )
    
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, page, loadImages])

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxIndex(null)
    document.body.style.overflow = ''
  }

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (lightboxIndex === null) return
    if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1)
    } else if (direction === 'next' && lightboxIndex < images.length - 1) {
      setLightboxIndex(lightboxIndex + 1)
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') navigateLightbox('prev')
      if (e.key === 'ArrowRight') navigateLightbox('next')
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex])

  // Loading state
  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Spinner className="animate-spin text-gray-400" size={48} />
        <p className="text-gray-500 text-lg">Loading gallery...</p>
      </div>
    )
  }

  const portraitCount = images.filter(img => img.orientation === 'portrait').length
  const landscapeCount = images.filter(img => img.orientation === 'landscape').length

  return (
    <div>
      {/* Stats bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <Images weight="bold" />
          <span><strong>{total}</strong> photos</span>
        </div>
        <div className="stat-item">
          <ImageIcon weight="bold" />
          <span><strong>{portraitCount}</strong> portrait</span>
        </div>
        <div className="stat-item">
          <Camera weight="bold" />
          <span><strong>{landscapeCount}</strong> landscape</span>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-8 text-center">
          <LayerCard className="p-6 max-w-md mx-auto text-red-600">
            <p className="font-semibold mb-2">Error loading gallery</p>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <Button variant="primary" onClick={() => loadImages(1)}>
              Try Again
            </Button>
          </LayerCard>
        </div>
      )}

      {/* Gallery grid */}
      <div className="gallery-grid">
        {images.map((image, index) => (
          <ImageCard
            key={image.key}
            image={image}
            onClick={() => openLightbox(index)}
          />
        ))}
      </div>

      {/* Loading indicator at bottom */}
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner className="animate-spin text-gray-400" size={32} />
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} className="h-4" />

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          image={images[lightboxIndex]}
          currentIndex={lightboxIndex}
          total={images.length}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox('prev')}
          onNext={() => navigateLightbox('next')}
        />
      )}
    </div>
  )
}

function ImageCard({
  image,
  onClick,
}: {
  image: GalleryImage
  onClick: () => void
}) {
  const [loaded, setLoaded] = useState(false)

  return (
    <LayerCard
      className="image-card"
      onClick={onClick}
    >
      {!loaded && (
        <div className="absolute inset-0 skeleton-pulse bg-gray-200 flex items-center justify-center z-10">
          <ImageIcon className="text-gray-400" size={32} />
        </div>
      )}
      <img
        src={image.thumbnailUrl}
        alt={image.filename}
        loading="lazy"
        className="lazy-image"
        style={{ 
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </LayerCard>
  )
}

function Lightbox({
  image,
  currentIndex,
  total,
  onClose,
  onPrev,
  onNext,
}: {
  image: GalleryImage
  currentIndex: number
  total: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}) {
  const [rotation, setRotation] = useState(0)
  
  // Reset rotation when image changes
  useEffect(() => {
    setRotation(0)
  }, [currentIndex])
  
  const handleRotate = (e: React.MouseEvent) => {
    e.stopPropagation()
    setRotation((prev) => (prev + 90) % 360)
  }

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>
        <X size={24} weight="bold" />
      </button>

      <button 
        className="lightbox-rotate" 
        onClick={handleRotate}
        title="Rotate 90°"
      >
        <ArrowsClockwise size={24} weight="bold" />
      </button>

      {currentIndex > 0 && (
        <button className="lightbox-nav prev" onClick={(e) => { e.stopPropagation(); onPrev() }}>
          <CaretLeft size={24} weight="bold" />
        </button>
      )}

      {currentIndex < total - 1 && (
        <button className="lightbox-nav next" onClick={(e) => { e.stopPropagation(); onNext() }}>
          <CaretRight size={24} weight="bold" />
        </button>
      )}

      <img
        src={image.fullUrl}
        alt=""
        className="lightbox-image"
        style={{ 
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      />

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm opacity-80">
        {currentIndex + 1} / {total}
      </div>
    </div>
  )
}
